import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted, watch } from 'vue';
import { FindLocationSortOptions, FindLocationsUsingAccounts, FindLocationsUsingPreference, ILocationQueryService, ISearchService, Listing, ListingType, SearchRequestBase } from '@truckdown/locations';
import { RegionModel, ServiceModel } from '@truckdown/systems';
import { LocationIdModel } from '@truckdown/servicetransactions';

interface ILastKey {
    lastId?: string,
    lastCountry?: string,
    lastRegion?: string,
    lastCity?: string
}

interface IData {
    list: Ref<Listing[]>,
    total: Ref<number>,
    loading: Ref<boolean>,
    showMore: Ref<boolean>,
    moreWarning: Ref<string>,
    moreBlock: Ref<boolean>,
    moreCaptcha: Ref<boolean>,
    regionFilter: Ref<string>,
    published: Ref<boolean>,
    pages: Ref<ILastKey[]>,
    current: Ref<ILastKey>
}

const getData = function (props: IProps) {
    return {
        list: ref<Listing[]>(props.listings as Listing[]),
        total: ref<number>(props.total ?? 0),
        loading: ref<boolean>(false),
        showMore: ref<boolean>(props.search !== undefined
            && context.isAuthenticated
            && props.listings.length >= 10),
        moreWarning: ref<string>(''),
        moreBlock: ref<boolean>(false),
        moreCaptcha: ref<boolean>(false),
        regionFilter: ref<string>(''),
        published: ref<boolean>(false),
        pages: ref<ILastKey[]>([]),
        current: ref<ILastKey>({})
    };
}

const getComputed = function (data: IData, props: IProps) {
    let regions = (props.allRegions ?? []) as RegionModel[];
    return {
        lazyListings: computed(() => {
            if (props.loadFirst > 0) {
                return data.list.value.slice(props.loadFirst);
            } else {
                return data.list.value;
            }
        }),
        preListings: computed(() => {
            if (props.loadFirst > 0) {
                return data.list.value.slice(0, props.loadFirst);
            } else {
                return [];
            }
        }),
        pagerText: computed(() => {
            let previous = (data.pages.value.length * 10);
            let start = previous + 1;
            let end = previous + data.list.value.length;
            return 'Displaying ' + start + ' to ' + end + ' of ' + data.total.value;
        }),
        showFilter: computed(() => {
            return props.actQuery !== undefined
                || props.prefQuery !== undefined;
        }),
        showPublished: computed(() => {
            return props.actQuery !== undefined;
        }),
        enablePrevious: computed(() => {
            return !data.loading.value && data.pages.value.length > 0;
        }),
        enableNext: computed(() => {
            let previous = (data.pages.value.length * 10);
            return !data.loading.value && (previous + data.list.value.length) < data.total.value;
        }),
        caRegions: computed(() => {
            return regions
                .filter((r) => {
                    return r.country == 'CA';
                });
        }),
        usRegions: computed(() => {
            return regions
                .filter((r) => {
                    return r.country == 'US';
                });
        }),
        displayIcon: computed(() => {
            if (props.icon && props.icon.length > 0) {
                return props.icon;
            }
            return 'faLocationDot';
        })
    };
}

const runListingQuery = async function (qry: FindLocationsUsingAccounts | FindLocationsUsingPreference, data: IData, lastKey?: ILastKey) {
    try {
        let service = await context.getService<ILocationQueryService>('ILocationQueryService');
        let res = await service.findListings(qry);
        //TODO: handle service requests?
        if (lastKey) {
            if (lastKey.lastId == data.list.value[data.list.value.length - 1].id) {
                //next
                data.pages.value.push(data.current.value);
            } else if (lastKey.lastId != data.current.value.lastId) {
                //previous
                data.pages.value.pop();
            }
            data.current.value = lastKey;
            data.total.value = res.count;
            data.list.value = res.results;
        } else {
            data.pages.value = [];
            data.current.value = {};
            data.total.value = res.count;
            data.list.value = res.results;
        }
    }
    catch (err) {
        console.log('Error querying locations', err);
    }
    finally {
        data.loading.value = false;
    }
}

const applyOptions = function (qry: FindLocationsUsingAccounts | FindLocationsUsingPreference, data: IData, lastKey?: ILastKey) {
    if (lastKey) {
        qry.lastCity = lastKey.lastCity;
        qry.lastCountry = lastKey.lastCountry;
        qry.lastRegion = lastKey.lastRegion;
        qry.lastId = lastKey.lastId;
    }

    if (data.regionFilter.value.length > 0) {
        if ((qry as FindLocationsUsingPreference).type) {
            (qry as FindLocationsUsingPreference).regionCode = data.regionFilter.value;
        } else {
            qry.tags.push('#' + data.regionFilter.value.toLowerCase());
        }
    }
}

const runAccountQuery = function (qry: FindLocationsUsingAccounts, data: IData, lastKey?: ILastKey) {
    let newQry = {
        accountIds: qry.accountIds,
        tags: ['#active', '||#pending_approval'],
        limit: 10,
        sortOption: qry.sortOption,
        options: qry.options
    } as FindLocationsUsingAccounts;

    if (data.published.value) {
        newQry.tags.push('#published');
    }
    applyOptions(newQry, data, lastKey);

    runListingQuery(newQry, data, lastKey);
}

const runPreferenceQuery = function (qry: FindLocationsUsingPreference, data: IData, lastKey?: ILastKey) {
    let newQry = {
        type: qry.type,
        tags: ['#active', '#published'],
        limit: 10,
        sortOption: qry.sortOption,
        options: qry.options
    } as FindLocationsUsingPreference;

    applyOptions(newQry, data, lastKey);

    runListingQuery(newQry, data, lastKey);
}

const runQuery = function (props: IProps, data: IData, lastKey?: ILastKey) {
    if (props.actQuery) {
        runAccountQuery(props.actQuery as FindLocationsUsingAccounts, data, lastKey);
    } else if (props.prefQuery) {
        runPreferenceQuery(props.prefQuery as FindLocationsUsingPreference, data, lastKey);
    } else {
        data.loading.value = false;
    }
}

const getMethods = function (props: IProps, data: IData) {
    return {
        enableMore: function (ev: Event) {
            data.moreCaptcha.value = false;
            data.showMore.value = true;
            this.loadMore(ev);
        },
        loadMore: async function (ev: Event) {
            let query = props.search as SearchRequestBase;
            if (!data.loading.value && query) {
                data.loading.value = true;

                try {
                    let service = await context.getService<ISearchService>('ISearchService');
                    query.page = (query.page ?? 1) + 1;
                    let res = await service.search(query);
                    if (res.blocked) {
                        data.moreBlock.value = true;
                        data.showMore.value = false;
                    } else if (res.captcha) {
                        data.moreCaptcha.value = true;
                        data.showMore.value = false;
                    } else if (res.result) {
                        context.analytics({
                            'event': 'Search',
                            'event_category': 'Search',
                            'event_label': 'More Results'
                        });
                        //TODO: handle service requests ....
                        data.moreWarning.value = res.warning;
                        if (res.result.listings.length > 0) {
                            data.list.value.push(...res.result.listings)
                            context.events.emit('add-map-listings', res.result.listings);
                        }
                        if (res.result.listings.length < 10) {
                            data.showMore.value = false;
                        }
                    }
                }
                catch (err) {
                    console.log(err);
                    data.showMore.value = false;
                }
                finally {
                    data.loading.value = false;
                }
            }
        },
        onPrevious: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastKey = data.pages.value[data.pages.value.length - 1];
                runQuery(props, data, lastKey);
            }
        },
        onNext: function (ev: Event) {
            if (!data.loading.value) {
                data.loading.value = true;

                let lastListing = data.list.value[data.list.value.length - 1];
                let lastKey = {
                    lastCountry: lastListing.location.countryCode,
                    lastRegion: lastListing.location.regionCode,
                    lastCity: lastListing.location.city,
                    lastId: lastListing.id
                } as ILastKey;
                runQuery(props, data, lastKey);
            }
        },
        getListingClass: function (type: ListingType) {
            switch (type) {
                case ListingType.Elite: {
                    return 'full';
                }
                case ListingType.Pro: {
                    return 'full';
                }
                case ListingType.Branded: {
                    return 'full';
                }
                default: {
                    return '';
                }
            }
        },
        getPromoId: function (listingId: string) {
            if (props.promotionIds) {
                return props.promotionIds[listingId];
            }
            return undefined;
        }
    };
}

const configureWatches = function (props: any, data: IData) {
    watch(data.regionFilter, function (val: string, old: string) {
        if (data.loading.value) {
            data.regionFilter.value = old;
        } else {
            data.loading.value = true;
            runQuery(props, data);
        }
    });
    watch(data.published, function (val: boolean, old: boolean) {
        if (data.loading.value) {
            data.published.value = old;
        } else {
            data.loading.value = true;
            runQuery(props, data);
        }
    });
}

interface IProps {
    name?: string;
    display?: string;
    total?: number;
    allServices: ServiceModel[],
    allRegions: RegionModel[],
    listings: Listing[],
    search?: SearchRequestBase,
    actQuery?: FindLocationsUsingAccounts,
    prefQuery?: FindLocationsUsingPreference,
    enableListingMaps: boolean,
    icon?: string,
    loadFirst: number,
    promotionIds: { [key: string]: string },
    disableRequests: boolean
}

export default defineComponent({
    props: {
        name: String,
        display: String,
        total: Number,
        allServices: {
            type: Array as () => ServiceModel[],
            default: []
        },
        allRegions: {
            type: Array as () => RegionModel[],
            default: [],
            required: false
        },
        listings: {
            type: Array as () => Listing[],
            required: true
        },
        search: {
            type: Object as () => SearchRequestBase,
            required: false
        },
        actQuery: {
            type: Object as () => FindLocationsUsingAccounts,
            required: false
        },
        prefQuery: {
            type: Object as () => FindLocationsUsingPreference,
            required: false
        },
        enableListingMaps: Boolean,
        icon: String,
        loadFirst: {
            type: Number,
            default: 0
        },
        promotionIds: {
            type: Object as () => { [key: string]: string },
            default: {}
        },
        disableRequests: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'add-map-listings'
    ],
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(data, props);
        let methods = getMethods(props, data);

        onMounted(() => {
            if (props.search && props.name == 'listings') {
                context.analytics({
                    'event': 'Search',
                    'event_category': 'Search',
                    'event_label': 'Results'
                });
            }
        });

        context.events.on('move-listing', (from: string, to: string, listing: Listing) => {
            if (from && to && listing) {
                if (from === props.name) {
                    data.loading.value = true;
                    data.list.value = data.list.value.filter((itm) => {
                        return itm.id !== listing.id;
                    });
                    let lastKey = data.current.value;
                    runQuery(props, data, lastKey);
                }
                if (to === props.name) {
                    data.loading.value = true;
                    if (!data.list.value.some((itm) => {
                        return itm.id == listing.id;
                    })) {
                        data.list.value.unshift(listing);
                    }
                    let lastKey = data.current.value;
                    runQuery(props, data, lastKey);
                }
            }
        });
        configureWatches(props, data);

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
